import type { SearchExchangeCode } from './facetOptions';
import { EXCHANGE_GROUPS } from './facetOptions';
import {
  CountBubble,
  FacetApplyPopover,
  FacetCheckboxButton,
  useSyncedPendingFacetState,
} from './searchFacetFilterPrimitives';
import {
  ALL_EXCHANGE_CODES,
  getExchangeTriggerLabel,
  getFilteredExchangeGroups,
  getGroupCheckedState,
  getToggledExchangeGroupValues,
  getToggledExchangeValues,
} from './searchFacetFilterUtils';

function ExchangeTriggerContent({
  selectedExchangeCodes,
}: {
  selectedExchangeCodes: SearchExchangeCode[];
}) {
  const selectedCount = selectedExchangeCodes.length;

  return (
    <>
      <span className="min-w-0 truncate">Exchange</span>
      {selectedCount > 0 && selectedCount < ALL_EXCHANGE_CODES.length ? (
        <CountBubble count={selectedCount} />
      ) : null}
    </>
  );
}

export function ExchangeFacetMenu({
  selectedExchangeCodes,
  triggerClassName,
  popupClassName,
  popupMatchTrigger,
  onApply,
}: {
  selectedExchangeCodes: SearchExchangeCode[];
  triggerClassName?: string;
  popupClassName?: string;
  popupMatchTrigger?: boolean;
  onApply: (exchangeCodes: SearchExchangeCode[] | undefined) => void;
}) {
  const selectedExchangeCodeKey = selectedExchangeCodes.join('|');
  const [pendingExchangeCodes, setPendingExchangeCodes] =
    useSyncedPendingFacetState(selectedExchangeCodes, selectedExchangeCodeKey);
  const pendingExchangeValues = new Set(pendingExchangeCodes);
  const resetPendingExchangeCodes = () => {
    setPendingExchangeCodes(selectedExchangeCodes);
  };

  return (
    <FacetApplyPopover
      triggerClassName={triggerClassName}
      triggerTrackProps={{
        'data-track': 'search_facet_open',
        'data-track-label': 'Exchange',
        'data-track-context': 'search_facet',
      }}
      popupClassName={popupClassName}
      popupMatchTrigger={popupMatchTrigger}
      label={getExchangeTriggerLabel(selectedExchangeCodes)}
      triggerContent={
        <ExchangeTriggerContent selectedExchangeCodes={selectedExchangeCodes} />
      }
      searchPlaceholder="Search exchanges"
      onClear={() => {
        setPendingExchangeCodes([]);
      }}
      onClose={resetPendingExchangeCodes}
      onApply={() => {
        onApply(
          pendingExchangeCodes.length === 0 ||
            pendingExchangeCodes.length === ALL_EXCHANGE_CODES.length
            ? undefined
            : pendingExchangeCodes,
        );
      }}
      onSelectAll={() => {
        setPendingExchangeCodes(ALL_EXCHANGE_CODES);
      }}
      applyDisabled={pendingExchangeCodes.length === 0}
    >
      {({ searchQuery }) => {
        const filteredGroups = getFilteredExchangeGroups(
          EXCHANGE_GROUPS,
          searchQuery,
        );

        return filteredGroups.map((group) => {
          const groupState = getGroupCheckedState(group, pendingExchangeValues);

          return (
            <div key={group.value}>
              <FacetCheckboxButton
                label={group.label}
                checked={groupState.checked}
                indeterminate={groupState.indeterminate}
                emphasized
                onClick={() => {
                  setPendingExchangeCodes((current) =>
                    getToggledExchangeGroupValues(group, current),
                  );
                }}
              />

              <div>
                {group.exchanges.map((exchange) => (
                  <FacetCheckboxButton
                    key={exchange.value}
                    label={exchange.label}
                    checked={pendingExchangeValues.has(exchange.value)}
                    nested
                    onClick={() => {
                      setPendingExchangeCodes((current) =>
                        getToggledExchangeValues(current, exchange.value),
                      );
                    }}
                  />
                ))}
              </div>
            </div>
          );
        });
      }}
    </FacetApplyPopover>
  );
}
